import type { CoverColor } from '../../types'
import type { FaceLandmarks } from '../../hooks/useFaceDetection'
import styles from './GlassesOverlay.module.css'

interface GlassesOverlayProps {
  landmarks: FaceLandmarks
  videoWidth: number
  videoHeight: number
  selectedColor: CoverColor
}

const COVER_IMAGES: Record<CoverColor, string> = {
  blue:   '/brandbook/35_blue.svg',
  yellow: '/brandbook/36_yellow.svg',
  green:  '/brandbook/37_green.svg',
  red:    '/brandbook/38_red.svg',
}

export function GlassesOverlay({
  landmarks,
  videoWidth,
  videoHeight,
  selectedColor,
}: GlassesOverlayProps) {
  const { leftEye, rightEye, faceWidth, faceAngle } = landmarks

  // Convert normalized coordinates to pixels
  const leftEyeX  = leftEye.x  * videoWidth
  const leftEyeY  = leftEye.y  * videoHeight
  const rightEyeX = rightEye.x * videoWidth
  const rightEyeY = rightEye.y * videoHeight

  // Center point between both eyes
  const centerX = (leftEyeX + rightEyeX) / 2
  const centerY = (leftEyeY + rightEyeY) / 2

  // Size: 1.55× the interocular face width so the frame extends to the temples
  const glassesWidth  = faceWidth * videoWidth * 1.55
  // Height bounding box — generous so the PNG maintains its natural ratio via preserveAspectRatio
  const glassesHeight = glassesWidth * 0.5

  return (
    <svg
      className={styles.overlay}
      viewBox={`0 0 ${videoWidth} ${videoHeight}`}
      preserveAspectRatio="xMidYMid slice"
    >
      {/*
        Translate to eye center, rotate to match face tilt,
        then offset the image so it's centered on that point.
      */}
      <g transform={`translate(${centerX}, ${centerY}) rotate(${faceAngle})`}>
        <image
          href={COVER_IMAGES[selectedColor]}
          x={-glassesWidth / 2}
          y={-glassesHeight / 2}
          width={glassesWidth}
          height={glassesHeight}
          preserveAspectRatio="xMidYMid meet"
        />
      </g>
    </svg>
  )
}
